import { supabase } from './supabase'

/**
 * Fetch wrapper for /api/* proxy endpoints.
 * Automatically attaches the Supabase JWT.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    },
  })

  if (!res.ok) {
    const text = await res.text()
    const trimmed = text.trimStart()
    const isHtml = trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')

    if (isHtml) {
      const localApiHint =
        typeof window !== 'undefined' && window.location.hostname === 'localhost' && path.startsWith('/api/')
          ? ' Make sure the local Pages Functions server is running on port 8788.'
          : ''
      throw new Error(`API returned HTML (${res.status}) instead of JSON.${localApiHint}`)
    }

    let apiError: string | undefined
    try {
      const json = JSON.parse(text) as { error?: string }
      apiError = typeof json.error === 'string' ? json.error : undefined
    } catch {
      // Fall back to the raw response text below.
    }

    throw new Error(apiError || `API error ${res.status}: ${text}`)
  }

  const contentType = res.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    return res.json()
  }
  return res.text() as unknown as T
}
